import {
  DurationVital,
  DurationVitalStopParams,
} from 'src/types/datadogRumApi';
import {
  DurationVitalOptions,
  DurationVitalReference,
  getWindow,
} from 'src/utilities/window';

export type DataDogRumUser = {
  id: string;
};

export const configureRumUser = (userData: DataDogRumUser): void => {
  const window = getWindow();

  if (window.DD_RUM) {
    window.DD_RUM.onReady(() => {
      window.DD_RUM.setUser(userData);
    });
  }
};

export type DataDogRumCorpContext = {
  corpId: string;
  corpName: string;
  corpUserRole: string;
  corpType: string;
};

export const setRumGlobalContext = (context: DataDogRumCorpContext): void => {
  const window = getWindow();

  if (!window.DD_RUM) {
    return; // DataDog RUM is not initialized, nothing to do.
  }

  window.DD_RUM.onReady(() => {
    // logic based off of grub-telemetry's handling of updating RUM context
    // https://github.com/GrubhubProd/subapp-monorepo/blob/main/projects/core/libraries/grub-telemetry-core-external-bridge/src/datadog/datadog-event-handlers.ts#L55
    const currentContext = window.DD_RUM.getGlobalContext();
    const hasGlobal = typeof currentContext?.global === 'object';
    const existingGlobal = hasGlobal ? currentContext.global : {};

    const mergedGlobal = { ...existingGlobal, ...context };

    window.DD_RUM.setGlobalContextProperty('global', mergedGlobal);
  });
};

/**
 * Creates a duration vital tracker for measuring how long an operation takes.
 * Returns `undefined` when DataDog RUM is not initialized.
 *
 * By name (single concurrent operation):
 * ```ts
 * const vital = durationVital('page_load');
 * vital?.start();
 * vital?.stop();
 * ```
 *
 * By reference (multiple concurrent operations with the same name):
 * ```ts
 * const vital = durationVital('api_call');
 * const ref = await vital?.start({ context: { endpoint: '/users' } });
 * vital?.stop({ reference: ref });
 * ```
 */
export const durationVital = (name: string): DurationVital | undefined => {
  const window = getWindow();

  if (!window.DD_RUM) {
    return undefined;
  }

  return {
    start: async (
      options?: DurationVitalOptions
    ): Promise<DurationVitalReference> => {
      return new Promise((resolve) => {
        window.DD_RUM.onReady(() => {
          resolve(window.DD_RUM.startDurationVital(name, options));
        });
      });
    },
    stop: (params?: DurationVitalStopParams): void => {
      const { reference, options } = params ?? {};

      window.DD_RUM.onReady(() => {
        window.DD_RUM.stopDurationVital(reference ?? name, options);
      });
    },
  };
};
